import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { Trash2, FileText, ExternalLink, Loader2, Info } from 'lucide-react';
import PropTypes from 'prop-types';
import API from '@/apiRequest';
import { showToast } from '@/store/ducks/toasts';
import DocumentsCircle from '@/svg-icons/documents-circle';

const MAX_SPLIT_SIZE_BYTES = 25 * 1024 * 1024;
const isHttpUrl = val => typeof val === 'string' && /^https?:\/\//i.test(val);
const getDocumentUrl = doc => (isHttpUrl(doc?.url) ? doc.url : null);
const getExtension = fileName => (fileName ? fileName.split('.').pop().toLowerCase() : 'pdf');

// Uploads the file directly to storage and returns a presigned download URL.
// Large files would otherwise travel as Base64 inside the order JSON and break the API.
const uploadDocument = async file => {
  const extension = getExtension(file.name);
  const { data } = await API.get('/upload', { params: { type: 'signature_document', extension } });
  await axios.put(data.url, file, {
    headers: {
      'Content-Type': file.type || 'application/pdf',
      'x-amz-acl': 'public-read-write',
    },
  });
  return { url: data.download_url, extension, name: file.name };
};

const DocuSignFile = ({ onChange, formData, formContext, schema, name }) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [documents, setDocuments] = useState(formData || []);
  const [documentSizes, setDocumentSizes] = useState({});
  const lastEmittedRef = useRef('');
  const dispatch = useDispatch();
  const intl = useIntl();

  const schemaItems = schema?.items;
  const schemaProps =
    typeof schemaItems === 'object' && schemaItems !== null && !Array.isArray(schemaItems)
      ? schemaItems.properties || {}
      : {};
  const isBry = Object.prototype.hasOwnProperty.call(schemaProps, 'base64document');
  const contentKey = isBry ? 'base64document' : 'url';
  const isOrderEdition = formContext?.isNewOrder === false;

  const toDocument = d => ({
    ...(d[contentKey] && { [contentKey]: d[contentKey] }),
    ...(d.url && { url: d.url }),
    ...(d.base64document && { base64document: d.base64document }),
    extension: d.extension || getExtension(d.name),
    name: d.name,
    ...(d.id && { id: d.id }),
    ...(d.status && { status: d.status }),
  });

  useEffect(() => {
    // Sync external formData into internal documents when changed from outside
    if (
      Array.isArray(formData) &&
      formData.length > 0 &&
      JSON.stringify(formData) !== lastEmittedRef.current &&
      JSON.stringify(formData) !== JSON.stringify(documents)
    ) {
      setDocuments(formData.map(toDocument));
    }
  }, [formData]);

  const normalize = docs =>
    docs.filter(d => d.status !== 'deleted' && d.status !== 'DELETED').map(toDocument);

  useEffect(() => {
    formContext?.setIsFileUploading?.(isUploading);
    return () => {
      formContext?.setIsFileUploading?.(false);
    };
  }, [isUploading, formContext]);

  const handleFiles = async files => {
    if (!files?.length || isUploading) return;
    setIsUploading(true);
    const updatedDocuments = [...documents];
    try {
      // Sequential uploads keep memory and bandwidth usage predictable for large files
      for (const file of files) {
        const uploaded = await uploadDocument(file);
        updatedDocuments.push(uploaded);
        setDocumentSizes(prev => ({
          ...prev,
          [uploaded.name]: file.size,
          ...(uploaded.url ? { [uploaded.url]: file.size } : {}),
        }));
        const normalized = normalize(updatedDocuments);
        lastEmittedRef.current = JSON.stringify(normalized);
        setDocuments([...updatedDocuments]);
        onChange(normalized);
      }
    } catch (err) {
      console.error('[DocuSignFile] Upload error:', err);
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: intl.formatMessage({ id: 'ERROR_UPLOADING_FILE' }),
          duration: 5000,
        })
      );
    } finally {
      setIsUploading(false);
    }
  };

  const deleteHandler = docToDelete => {
    const updatedDocuments = [...documents];
    const index = updatedDocuments.findIndex(
      d =>
        d === docToDelete ||
        (docToDelete.id && d.id === docToDelete.id) ||
        (d.name === docToDelete.name &&
          (d.url || d.base64document) === (docToDelete.url || docToDelete.base64document))
    );
    if (index === -1) return;

    if (docToDelete.id) {
      const statusEnum = schemaItems?.properties?.status?.enum || [];
      const deletedValue = statusEnum.includes('DELETED') ? 'DELETED' : 'deleted';
      updatedDocuments[index] = {
        ...updatedDocuments[index],
        status: deletedValue,
      };
    } else {
      updatedDocuments.splice(index, 1);
    }
    const normalized = normalize(updatedDocuments);
    lastEmittedRef.current = JSON.stringify(normalized);
    setDocuments(updatedDocuments);
    onChange(normalized);
  };

  const handleSelectClick = () => {
    if (isOrderEdition) return;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (e) => {
    if (isOrderEdition) return;
    const files = Array.from(e.target.files || []);
    if (files.length) handleFiles(files);
  };

  const handleDragOver = (e) => {
    if (isOrderEdition) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    if (isOrderEdition) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    if (isOrderEdition) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []).filter((f) => !!f);
    if (files.length) handleFiles(files);
  };

  const visibleDocs = useMemo(
    () => documents.filter(d => d.status !== 'deleted' && d.status !== 'DELETED'),
    [documents]
  );

  const probedUrlsRef = useRef(new Set());

  useEffect(() => {
    let isMounted = true;
    visibleDocs.forEach(doc => {
      const url = getDocumentUrl(doc);
      if (url && typeof doc.size !== 'number' && !probedUrlsRef.current.has(url)) {
        probedUrlsRef.current.add(url);
        fetch(url, { method: 'GET', headers: { Range: 'bytes=0-0' } })
          .then(res => {
            const range = res.headers.get('content-range');
            if (range && isMounted) {
              const total = parseInt(range.split('/')[1], 10);
              if (!isNaN(total) && total > 0) {
                setDocumentSizes(prev => ({ ...prev, [url]: total, [doc.name]: total }));
              }
            }
          })
          .catch(() => {});
      }
    });

    return () => {
      isMounted = false;
    };
  }, [visibleDocs]);

  const hasLargeDocument = useMemo(() => {
    return visibleDocs.some(doc => {
      const url = getDocumentUrl(doc);
      const size = (url && documentSizes[url]) || documentSizes[doc.name];
      if (typeof size === 'number' && size > MAX_SPLIT_SIZE_BYTES) {
        return true;
      }
      if (doc.base64document) {
        const approxBytes = Math.round((doc.base64document.length * 3) / 4);
        if (approxBytes > MAX_SPLIT_SIZE_BYTES) return true;
      }
      return false;
    });
  }, [visibleDocs, documentSizes]);

  return (
    <div className="flex flex-row w-full pr-2 font-['Open_Sans']">
      {/* Left column: icon */}
      <DocumentsCircle
        viewBox="0 0 34.016 34.016"
        width="20"
        height="20"
        style={{ flexShrink: 0 }}
      />

      {/* Right column: all content */}
      <div className="flex-1 pl-2">
        <p className="mb-[0.6rem] font-bold text-[calc(10px+0.4rem)] font-['Open_Sans']">
          <FormattedMessage id={name} />
        </p>

        {!isOrderEdition && (
          <>
            <label
              className="block mb-2 font-semibold text-[14px] text-[#22324d] font-['Open_Sans']"
              htmlFor="dropzone"
            >
              <FormattedMessage id="Docusign.file.toSign" />
            </label>

            <div
              id="dropzone"
              data-cy="dropzone"
              onClick={handleSelectClick}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`w-full flex items-center justify-center gap-3 py-6 px-8 border-2 border-dashed rounded-[12px] cursor-pointer transition-all duration-200 ${
                isDragging
                  ? 'border-cyan-400 bg-cyan-50'
                  : 'border-[#00bbdd] bg-[#f2fdff] hover:bg-[#e6f9fc] hover:shadow-[0_2px_12px_rgba(0,187,221,0.15)]'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png"
                onChange={handleFileInputChange}
                className="hidden"
                data-cy="file-input"
              />
              <span className="bg-[#00bbdd] hover:bg-[#009ebb] text-white px-6 py-[6px] text-sm font-semibold transition-colors rounded text-xs tracking-wide pointer-events-none">
                <FormattedMessage id="Docusign.select" />
              </span>
              <span className="text-[#00bbdd] text-sm font-semibold pointer-events-none">
                <FormattedMessage id="Docusign.drop" />
              </span>
            </div>

            {hasLargeDocument && (
              <div className="flex items-start gap-2.5 p-3 my-3 text-xs text-[#1e40af] bg-[#eff6ff] border border-[#bfdbfe] rounded-lg">
                <Info size={16} className="text-[#3b82f6] shrink-0 mt-0.5" />
                <span>
                  <FormattedMessage
                    id="Docusign.file.largeFileWarning"
                    defaultMessage="Arquivos com mais de 25 MB serão divididos automaticamente em partes para viabilizar o envio e a assinatura."
                  />
                </span>
              </div>
            )}
          </>
        )}

        {isUploading && (
          <div className="flex items-center gap-2 mt-2 text-sm text-[#5882C1]" data-cy="docusign-uploading">
            <Loader2 size={18} className="animate-spin" />
          </div>
        )}

        {visibleDocs.length > 0 && (
          <ul className="w-full list-none p-0 mt-2">
            {visibleDocs.map((document, index) => {
              const targetUrl = getDocumentUrl(document);
              return (
                <li
                  key={document.id || index}
                  className="flex items-center justify-between py-1 border-b border-gray-100 last:border-b-0"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <FileText size={18} className="text-[#393939] shrink-0" />
                    {targetUrl ? (
                      <a
                        href={targetUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 truncate text-sm text-[#00bbdd] hover:underline hover:text-[#009ebb] font-medium"
                        title={document.name}
                        onClick={e => e.stopPropagation()}
                      >
                        <span className="truncate">{document.name}</span>
                        <ExternalLink size={14} className="shrink-0 text-gray-400" />
                      </a>
                    ) : (
                      <span className="truncate text-sm text-gray-800" title={document.name}>
                        {document.name}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="w-6 h-6 flex items-center justify-center text-[#5882c1] hover:opacity-75 transition-colors border-none bg-white cursor-pointer shrink-0"
                    onClick={() => deleteHandler(document)}
                    disabled={isOrderEdition}
                    data-cy="delete-file"
                  >
                    <Trash2 size={16} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

DocuSignFile.propTypes = {
  onChange: PropTypes.func.isRequired,
  schema: PropTypes.object.isRequired,
  uiSchema: PropTypes.object,
  formData: PropTypes.array,
  formContext: PropTypes.object,
  name: PropTypes.string,
};

DocuSignFile.defaultProps = {
  formData: [],
  name: 'documents.title',
};

export default DocuSignFile;
